import { useEffect, useState } from "react";
import { ColumnItem } from "../../../common/models/table";
import { ActionsKey } from "../../../common/models/Actions";
import { HttpStatusCode } from "axios";
import { Loader } from "../../../common/components/Loader/Loader";
import { TitleHeader } from "../../../common/components/TitleHeader/TitleHeader";
import { LFTableV3 } from "../../../common/components/LFTable/LFTableV3";
import { CustomDrawer } from "../../../common/components/Drawer/CustomDrawer";
import { useGatePassListState, useGatePassStore } from "./store/_gatePassStore";
import { GatePassService } from "./store/_requests";
import { GatePassDetails } from "./components/GatePassDetails";
import { StatusTag } from "../../../common/components/Status/StatusTag";
import {
  GatePassStatusConstant,
  RemovalSourceConstant,
} from "./core/_constant.gatePass";
import { Spin } from "antd";
import { LoadingOutlined } from "@ant-design/icons";
import { downloadPDF } from "../../../utilities/downloadPdf";
import { Filters } from "../../container-yard/components/Filters";
import { API_URLs } from "../../../utilities/API_URLs";
import { getIdFromIri, getRowId } from "../../../common/utils/iri";
import { FaFileAlt } from "react-icons/fa";

export const StorageGatePasses = () => {
  const {
    setGatePassList,
    gatePassList,
    actioned,
    totalItems,
    setActioned,
  } = useGatePassStore();
  const { pageState, setPageState, resetPageState } = useGatePassListState();

  const [isShowForm, setIsShowForm] = useState<boolean>(false);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [downloadingId, setDownloadingId] = useState<string | null>(null);

  const cols: ColumnItem[] = [
    {
      key: "reference",
      title: "Référence",
      dataIndex: "reference",
      show: true,
    },
    {
      key: "uniqueIdentifierLoad",
      title: "N° Chargement",
      dataIndex: "uniqueIdentifierLoad",
      show: true,
    },
    {
      key: "removalSource",
      title: "Source",
      dataIndex: "removalSource",
      nestedKey: "removalSource.name",
      show: true,
      render: (removalSource: any) => {
        return (
          <StatusTag
            value={removalSource?.id}
            options={RemovalSourceConstant.OPTIONS}
          />
        );
      },
    },
    {
      key: "state",
      title: "Statut",
      dataIndex: "exitNote",
      show: true,
      render: (_: any, record: any) => {
        return (
          <StatusTag
            value={GatePassStatusConstant.getStatusKey(record)}
            options={GatePassStatusConstant.OPTIONS}
          />
        );
      },
    },
    {
      key: "quantityOut",
      title: "Tonnage (T)",
      dataIndex: "quantityOut",
      show: true,
    },
    {
      key: "bagCount",
      title: "Nombre de sorties",
      dataIndex: "bagCount",
      show: true,
    },
    {
      key: "customer",
      title: "Transitaire / Client",
      dataIndex: "customer",
      searchKey: "customer.name",
      show: true,
      render: (state: any) => state.name
    },
    {
      key: "particularTruckNumber",
      title: "Camion",
      dataIndex: "particularTruckNumber",
      nestedKey: "particularTruckNumber",
      show: true,
    },
    {
      key: "createdAt",
      title: "Date de l'enlèvement",
      dataIndex: "createdAt",
      // Filtre natif hérité de BaseEntity (DateFilter) : createdAt[after] /
      // createdAt[before]. GoodsRemovalControl n'expose aucun filtre partiel
      // sur reference / uniqueIdentifierLoad, la recherche ne peut donc pas
      // être textuelle comme sur GateOut.
      searchType: "date",
      show: true,
    },
  ];

  useEffect(() => {
    fetchAllGatePass(pageState);
  }, [pageState]);

  const onActionClick = async (item: any, action: ActionsKey) => {
    try {
      setIsLoading(true);

      const res = await GatePassService.getGatePassById(item?.id);

      if (res?.status === HttpStatusCode.Ok) {
        setActioned({
          data: {
            ...res?.data,
          },
          action,
        });

        setIsShowForm(true);
      }
    } catch (error) {
      console.log(error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleCloseDrawer = () => {
    setIsShowForm(false);
  };

  const fetchAllGatePass = async (params?: any) => {
    try {
      setIsLoading(true);
      const res = await GatePassService.getGatePassList(params);
      if (res?.status === HttpStatusCode.Ok) {
        setGatePassList(res?.data);
      }
    } catch (error) {
      console.log(error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleSetParams = (params: any) => {
    setPageState({
      ...params,
    });
  };

  const onClickExitNote = async (action: any) => {
    const rowData = action.row;
  
    const contentUrl = rowData?.exitNoteFile?.contentUrl;
    const basename = rowData?.exitNoteFile?.basename;
    const rowId = getRowId(rowData);
  
    if (!contentUrl) return;
  
    const fileName =
      basename?.split("?")[0] ||
      "bon-de-sortie.pdf";
  
    try {
      setDownloadingId(rowId);
  
      await downloadPDF(contentUrl, fileName);
    } catch (error) {
      console.error(error);
    } finally {
      setDownloadingId(null);
    }
  };

  return (
    <div className="">
      <Loader isLoading={isLoading} />
      <TitleHeader title="Enlèvement marchandises" />
      {/*
        Filtre sur l'origine de l'enlèvement (BOOKING / DIRECT).
        Attention : GoodsRemovalControl ne déclare pour l'instant que
        "storageLoad" et "customer" dans son #[ApiFilter(SearchFilter)], le
        paramètre "removalSource" est donc ignoré par l'API tant que
        '"removalSource" => "exact"' n'y est pas ajouté.
      */}
      <Filters
        uri={API_URLs.goodsRemovalControl.removalSourcesForFilters}
        filterKey="removalSource"
        params={pageState}
        initialParams={{
          page: 1,
          itemsPerPage: 10,
          pagination: true,
        }}
        onChange={handleSetParams}
      />
      <div className="mt-4">
        {/* Table section  */}
        <div className="">
          <LFTableV3
            columns={cols}
            data={gatePassList}
            params={pageState}
            onAction={onActionClick}
            isCanShow={() => true}
            onSearch={handleSetParams}
            onResetSearch={resetPageState}
            pagination={{
              defaultCurrentPage: pageState.page,
              itemsPerPage: pageState?.itemsPerPage,
              totalItems: totalItems,
            }}
            onChangePage={handleSetParams}
            otherActions={[
              (rowAction) => {
                const rowData = rowAction.row;
                const exitNoteId = getIdFromIri(rowData?.exitNoteFile?.["@id"]);
                const rowId = getRowId(rowData);

                if (!exitNoteId) {
                  return;
                }

                return (
                  <div>
                    {downloadingId === rowId ? (
                      <Spin
                        indicator={<LoadingOutlined spin />}
                        size="small"
                      />
                    ) : (
                      <div
                        className=" cursor-pointer"
                        onClick={() => {
                          onClickExitNote(rowAction);
                        }}
                      >
                        <FaFileAlt title={"Voir le bon de sortie"} />
                      </div>
                    )}
                  </div>
                );
              },
            ]}
          />
        </div>
      </div>

      {/* Drawer section  */}
      {isShowForm ? (
        <CustomDrawer
          isOpen={isShowForm}
          onClose={handleCloseDrawer}
          title={"Détails"}
          width={"75%"}
        >
          <GatePassDetails value={actioned?.data} />
        </CustomDrawer>
      ) : (
        ""
      )}
    </div>
  );
};
